import { useState, useCallback, useMemo } from 'react';

export type NotificationType = 'success' | 'error' | 'warning' | 'info';

export interface Notification {
  id: string;
  type: NotificationType;
  message: string;
  title?: string;
  duration?: number;
  createdAt: Date;
}

interface NotificationState {
  notifications: Notification[];
}

interface NotificationConfig {
  defaultDuration?: number;
}

export const useNotifications = ({ defaultDuration = 5000 }: NotificationConfig = {}) => {
  const [state, setState] = useState<NotificationState>({
    notifications: [],
  });

  const addNotification = useCallback(
    (notification: Omit<Notification, 'id' | 'createdAt'>) => {
      const id = Math.random().toString(36).substr(2, 9);
      const newNotification: Notification = {
        ...notification,
        id,
        createdAt: new Date(),
      };

      setState(prev => ({
        notifications: [...prev.notifications, newNotification],
      }));

      if (notification.duration !== 0) {
        setTimeout(() => {
          removeNotification(id);
        }, notification.duration || defaultDuration);
      }
    },
    [defaultDuration]
  );

  const removeNotification = useCallback((id: string) => {
    setState(prev => ({
      notifications: prev.notifications.filter(notification => notification.id !== id),
    }));
  }, []);

  const clearNotifications = useCallback(() => {
    setState({ notifications: [] });
  }, []);

  const success = useCallback(
    (message: string, title?: string, duration?: number) => {
      addNotification({ type: 'success', message, title, duration });
    },
    [addNotification]
  );

  const error = useCallback(
    (message: string, title?: string, duration?: number) => {
      addNotification({ type: 'error', message, title, duration });
    },
    [addNotification]
  );

  const warning = useCallback(
    (message: string, title?: string, duration?: number) => {
      addNotification({ type: 'warning', message, title, duration });
    },
    [addNotification]
  );

  const info = useCallback(
    (message: string, title?: string, duration?: number) => {
      addNotification({ type: 'info', message, title, duration });
    },
    [addNotification]
  );

  const notifications = useMemo(() => state.notifications, [state.notifications]);

  return {
    notifications,
    addNotification,
    removeNotification,
    clearNotifications,
    success,
    error,
    warning,
    info,
  };
}; 